import { usePostHog } from '@/contexts/PostHogProvider';
import { useAuth } from '@/hooks/useAuth';
import { useCallback, useRef } from 'react';

export const useAnalytics = () => {
  const { isInitialized, posthog } = usePostHog();
  const { user } = useAuth();
  const hasLoggedSkip = useRef(false);

  const track = useCallback((eventName: string, properties?: Record<string, any>) => {
    if (!isInitialized || !posthog) {
      if (!hasLoggedSkip.current) {
        console.debug('PostHog not initialized, skipping events until ready');
        hasLoggedSkip.current = true;
      }
      return;
    }

    const enrichedProperties = {
      ...properties,
      user_id: user?.id,
      user_email: user?.email,
      timestamp: new Date().toISOString(),
    };

    posthog.capture(eventName, enrichedProperties);
  }, [isInitialized, posthog, user]);

  // Authentication events
  const trackUserSignUp = useCallback((method: string = 'email') => {
    track('user_signed_up', { method });
  }, [track]);

  const trackUserLogin = useCallback((method: string = 'email') => {
    track('user_logged_in', { method });
  }, [track]);

  const trackUserLogout = useCallback(() => {
    track('user_logged_out');
  }, [track]);

  const trackAuthError = useCallback((errorType: string, errorMessage: string) => {
    track('auth_error', { error_type: errorType, error_message: errorMessage });
  }, [track]);

  // Onboarding events
  const trackOnboardingStarted = useCallback(() => {
    track('onboarding_started');
  }, [track]);

  const trackOnboardingCompleted = useCallback((data?: Record<string, any>) => {
    track('onboarding_completed', data);
  }, [track]);

  const trackSpiritualAssessmentCompleted = useCallback((assessment: Record<string, any>) => {
    track('spiritual_assessment_completed', assessment);
  }, [track]);

  // Journal events
  const trackJournalEntryStarted = useCallback(() => {
    track('journal_entry_started');
  }, [track]);

  const trackJournalEntryCompleted = useCallback((entryData?: Record<string, any>) => {
    track('journal_entry_completed', {
      content_length: entryData?.content?.length,
      has_spiritual_themes: entryData?.spiritual_themes?.length > 0,
      theme_count: entryData?.spiritual_themes?.length || 0,
    });
  }, [track]);

  const trackJournalEntryEdited = useCallback((entryId: string) => {
    track('journal_entry_edited', { entry_id: entryId });
  }, [track]);

  const trackJournalEntryDeleted = useCallback((entryId: string) => {
    track('journal_entry_deleted', { entry_id: entryId });
  }, [track]);

  const trackEntryFavorited = useCallback((entryId: string, isFavorited: boolean) => {
    track(isFavorited ? 'entry_favorited' : 'entry_unfavorited', { entry_id: entryId });
  }, [track]);

  // Spiritual guidance events
  const trackGuidanceRequested = useCallback((guidanceType: string, context?: Record<string, any>) => {
    track('guidance_requested', { 
      guidance_type: guidanceType,
      ...context 
    });
  }, [track]);

  const trackVerseInspirationViewed = useCallback((verse: string, context?: Record<string, any>) => {
    track('verse_inspiration_viewed', { 
      verse,
      ...context 
    });
  }, [track]);

  const trackScriptureReferenceClicked = useCallback((reference: string) => {
    track('scripture_reference_clicked', { reference });
  }, [track]);

  const trackSpiritualThemesGenerated = useCallback((themes: string[], entryId?: string) => {
    track('spiritual_themes_generated', { 
      themes,
      theme_count: themes.length,
      entry_id: entryId
    });
  }, [track]);

  const trackGuidanceError = useCallback((guidanceType: string, errorMessage: string) => {
    track('guidance_error', { guidance_type: guidanceType, error_message: errorMessage });
  }, [track]);

  // Dashboard and feature usage events
  const trackDashboardViewed = useCallback(() => {
    track('dashboard_viewed');
  }, [track]);

  const trackViewModeChanged = useCallback((mode: 'grid' | 'list' | 'calendar' | 'timeline') => {
    track(`${mode}_view_used`);
  }, [track]);

  const trackThemeFilterApplied = useCallback((theme: string) => {
    track('theme_filter_applied', { theme });
  }, [track]);

  const trackSpiritualJourneyWidgetViewed = useCallback(() => {
    track('spiritual_journey_widget_viewed');
  }, [track]);

  const trackStreakCalendarViewed = useCallback(() => {
    track('streak_calendar_viewed');
  }, [track]);

  // Export events
  const trackEntryExported = useCallback((format: 'pdf' | 'csv', entryCount?: number) => {
    track(`entry_exported_${format}`, { entry_count: entryCount });
  }, [track]);

  // Profile events
  const trackProfileUpdated = useCallback((updatedFields: string[]) => {
    track('profile_updated', { updated_fields: updatedFields });
  }, [track]);

  // Subscription events
  const trackTrialStarted = useCallback(() => {
    track('trial_started');
  }, [track]);

  const trackSubscriptionCreated = useCallback((plan: string) => {
    track('subscription_created', { plan });
  }, [track]);

  const trackPaymentSuccessful = useCallback((amount: number, plan: string) => {
    track('payment_successful', { amount, plan });
  }, [track]);

  const trackSubscriptionCancelled = useCallback((reason?: string) => {
    track('subscription_cancelled', { reason });
  }, [track]);

  const trackCustomerPortalAccessed = useCallback(() => {
    track('customer_portal_accessed');
  }, [track]);

  const trackPaymentMethodAdded = useCallback(() => {
    track('payment_method_added');
  }, [track]);

  // Page view tracking
  const trackPageView = useCallback((page: string, properties?: Record<string, any>) => {
    if (!isInitialized || !posthog) return;
    
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      page,
      ...properties,
    });
  }, [isInitialized, posthog]);

  return {
    // Core tracking
    track,
    trackPageView,
    
    // Authentication
    trackUserSignUp,
    trackUserLogin,
    trackUserLogout,
    trackAuthError,
    
    // Onboarding
    trackOnboardingStarted,
    trackOnboardingCompleted,
    trackSpiritualAssessmentCompleted,
    
    // Journal
    trackJournalEntryStarted,
    trackJournalEntryCompleted,
    trackJournalEntryEdited,
    trackJournalEntryDeleted,
    trackEntryFavorited,
    
    // Spiritual guidance
    trackGuidanceRequested,
    trackVerseInspirationViewed,
    trackScriptureReferenceClicked,
    trackSpiritualThemesGenerated,
    trackGuidanceError,
    
    // Dashboard and features
    trackDashboardViewed,
    trackViewModeChanged,
    trackThemeFilterApplied,
    trackSpiritualJourneyWidgetViewed,
    trackStreakCalendarViewed,
    
    // Export
    trackEntryExported,
    
    // Profile
    trackProfileUpdated,
    
    // Subscription
    trackTrialStarted,
    trackSubscriptionCreated,
    trackPaymentSuccessful,
    trackSubscriptionCancelled,
    trackCustomerPortalAccessed,
    trackPaymentMethodAdded,
    
    // Utilities
    isInitialized,
  };
};